import {
  DeviceSummary,
  DeviceDetail,
  BundleSummary,
  TenantSummary,
  HeartbeatResponse,
  AdminKillResponse,
  EnrollTokenResponse,
} from "../types";

const API_BASE = (
  (typeof process !== "undefined" && (process as any).env?.REACT_APP_API_BASE) ||
  (typeof window !== "undefined" && window.location.hostname === "localhost" ? "http://localhost:8080" : "https://cloud.pwrnetworks.com")
).replace(/\/$/, "");

class ApiClient {
  private apiKey: string = localStorage.getItem("mesh_api_key") || "";

  setApiKey(key: string) {
    this.apiKey = key;
    localStorage.setItem("mesh_api_key", key);
  }

  getApiKey(): string {
    return this.apiKey;
  }

  private headers(): Record<string, string> {
    const h: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (this.apiKey) {
      h["X-API-Key"] = this.apiKey;
    }
    return h;
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    signal?: AbortSignal
  ): Promise<T> {
    const url = path.startsWith("/healthz") ? `${API_BASE}${path}` : `${API_BASE}/v1${path}`;
    const res = await fetch(url, {
      method,
      headers: this.headers(),
      body: body ? JSON.stringify(body) : undefined,
      signal,
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(text || res.statusText);
    }
    const ct = res.headers.get("content-type") || "";
    if (ct.includes("application/json")) {
      return res.json() as Promise<T>;
    }
    return res.text() as unknown as T;
  }

  // Health
  healthz(): Promise<{ ok: boolean; service: string; version: string; time: string }> {
    return this.request("GET", "/healthz");
  }

  // Devices
  listDevices(tenantId?: string): Promise<DeviceSummary[]> {
    const q = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
    return this.request("GET", `/devices${q}`);
  }

  getDevice(id: string): Promise<DeviceDetail> {
    return this.request("GET", `/devices/${id}`);
  }

  getDeviceBundles(id: string): Promise<BundleSummary[]> {
    return this.request("GET", `/devices/${id}/bundles`);
  }

  // Tenants
  listTenants(): Promise<TenantSummary[]> {
    return this.request("GET", "/tenants");
  }

  // Enroll tokens
  createEnrollToken(
    name: string,
    role: string
  ): Promise<EnrollTokenResponse> {
    return this.request("POST", "/enroll-tokens", { name, role });
  }

  // Admin kill
  killDevice(deviceId: string, reason: string): Promise<AdminKillResponse> {
    return this.request("POST", "/admin/kill", { device_id: deviceId, reason });
  }

  // Real-time Event Stream (SSE)
  subscribeEvents(onMessage: (data: unknown) => void): () => void {
    const url = `${API_BASE}/v1/events?X-API-Key=${encodeURIComponent(this.apiKey)}`;
    const eventSource = new EventSource(url);
    eventSource.onmessage = (event) => {
      try {
        const parsed = JSON.parse(event.data);
        onMessage(parsed);
      } catch (err) {
        // ignore parse error
      }
    };
    return () => {
      eventSource.close();
    };
  }
}

export const api = new ApiClient();